import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquare, Trash2, CheckCircle, Clock, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { contactAPI } from '../../utils/api';

export const ContactManager = () => {
  const { toast } = useToast();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const fetchSubmissions = useCallback(async () => {
    try {
      const response = await contactAPI.getAll();
      setSubmissions(response.data);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast({
        title: "Error",
        description: "Failed to load contact submissions.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const handleUpdateStatus = async (id, newStatus) => {
    try {
      await contactAPI.updateStatus(id, newStatus);
      toast({
        title: "Status Updated",
        description: `Submission marked as ${newStatus}.`,
      });
      fetchSubmissions();
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: "Error",
        description: "Failed to update status.",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this submission?')) {
      try {
        await contactAPI.delete(id);
        toast({
          title: "Submission Deleted",
          description: "Contact submission has been removed.",
        });
        fetchSubmissions();
      } catch (error) {
        console.error('Error deleting submission:', error);
        toast({
          title: "Error",
          description: "Failed to delete submission.",
          variant: "destructive"
        });
      }
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'new':
        return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">New</Badge>;
      case 'read':
        return <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100">Read</Badge>;
      case 'responded':
        return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Responded</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading contact submissions...</div>;
  }

  const newCount = submissions.filter(s => s.status === 'new').length;
  const readCount = submissions.filter(s => s.status === 'read').length;
  const respondedCount = submissions.filter(s => s.status === 'responded').length;

  return (
    <div className="space-y-6" data-testid="contact-manager">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-2xl font-bold">Contact Form Submissions</h3>
          <div className="flex gap-4 text-sm text-slate-600 mt-1">
            <span>New: <span className="font-semibold text-blue-600">{newCount}</span></span>
            <span>Read: <span className="font-semibold text-yellow-600">{readCount}</span></span>
            <span>Responded: <span className="font-semibold text-emerald-600">{respondedCount}</span></span>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            setLoading(true);
            fetchSubmissions();
          }}
          className="border-slate-300"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="grid gap-6">
        {submissions.length === 0 ? (
          <Card className="border-2">
            <CardContent className="pt-6">
              <div className="text-center py-8 text-slate-500">
                No contact submissions yet.
              </div>
            </CardContent>
          </Card>
        ) : (
          submissions.map((sub) => (
            <Card 
              key={sub.id} 
              className={`border-2 ${sub.status === 'new' ? 'border-blue-200 bg-blue-50/30' : ''}`}
              data-testid={`contact-card-${sub.id}`}
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <MessageSquare className={`w-5 h-5 ${sub.status === 'new' ? 'text-blue-600' : 'text-emerald-600'}`} />
                      <h4 className="font-bold text-lg">{sub.name}</h4>
                      {getStatusBadge(sub.status)}
                    </div>
                    <div className="space-y-1 text-sm text-slate-600 mb-3">
                      <p>
                        <span className="font-medium">Email:</span>{' '}
                        <a href={`mailto:${sub.email}`} className="text-emerald-600 hover:underline">
                          {sub.email}
                        </a>
                      </p>
                      <p><span className="font-medium">Subject:</span> {sub.subject}</p>
                      <p><span className="font-medium">Date:</span> {new Date(sub.created_at).toLocaleString()}</p>
                    </div>
                    
                    {/* Message - expandable */}
                    <div className="bg-slate-50 rounded-lg p-4 mt-3">
                      <p className={`text-slate-700 ${expandedId !== sub.id ? 'line-clamp-2' : ''}`}>
                        {sub.message}
                      </p>
                      {sub.message.length > 150 && (
                        <button
                          onClick={() => setExpandedId(expandedId === sub.id ? null : sub.id)}
                          className="text-sm text-emerald-600 hover:underline mt-2"
                        >
                          {expandedId === sub.id ? 'Show less' : 'Read more'}
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2 ml-4">
                    {/* Status actions */}
                    {sub.status === 'new' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUpdateStatus(sub.id, 'read')}
                        title="Mark as Read"
                      >
                        <Clock className="w-4 h-4" />
                      </Button>
                    )}
                    {sub.status !== 'responded' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUpdateStatus(sub.id, 'responded')}
                        title="Mark as Responded"
                        className="border-emerald-300 text-emerald-600"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-red-300 text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(sub.id)}
                      data-testid={`delete-contact-${sub.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};
